import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Trash2, Plus, Minus, ShoppingBag, Zap } from 'lucide-react';
import { AppContext } from '../App';
import { toast } from 'sonner';
import { productUrl } from '../utils/slug';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const translations = {
  en: {
    yourCart: 'Your Cart',
    empty: 'Your cart is empty',
    emptyDesc: 'Browse our collection and add some stamps!',
    subtotal: 'Subtotal',
    shipping: 'Shipping',
    free: 'FREE',
    discount: 'Discount (10%)',
    total: 'Total',
    checkout: 'Checkout',
    continueShopping: 'Continue Shopping',
    browseShop: 'Browse Shop',
    items: 'items',
    item: 'item',
    remove: 'Remove',
    youMightLike: 'You Might Also Like',
    addToCart: 'Add',
  },
  tr: {
    yourCart: 'Sepetiniz',
    empty: 'Sepetiniz boş',
    emptyDesc: 'Koleksiyonumuza göz atın ve pul ekleyin!',
    subtotal: 'Ara Toplam',
    shipping: 'Kargo',
    free: 'ÜCRETSİZ',
    discount: 'İndirim (%10)',
    total: 'Toplam',
    checkout: 'Ödemeye Geç',
    continueShopping: 'Alışverişe Devam',
    browseShop: 'Mağazaya Git',
    items: 'ürün',
    item: 'ürün',
    remove: 'Kaldır',
    youMightLike: 'Bunları da Beğenebilirsiniz',
    addToCart: 'Ekle',
  },
  el: {
    yourCart: 'Το Καλάθι σας',
    empty: 'Το καλάθι σας είναι άδειο',
    emptyDesc: 'Περιηγηθείτε στη συλλογή μας!',
    subtotal: 'Υποσύνολο',
    shipping: 'Αποστολή',
    free: 'ΔΩΡΕΑΝ',
    discount: 'Έκπτωση (10%)',
    total: 'Σύνολο',
    checkout: 'Ολοκλήρωση',
    continueShopping: 'Συνέχεια Αγορών',
    browseShop: 'Κατάστημα',
    items: 'είδη',
    item: 'είδος',
    remove: 'Αφαίρεση',
    youMightLike: 'Μπορεί να σας αρέσουν',
    addToCart: 'Προσθήκη',
  },
  de: {
    yourCart: 'Ihr Warenkorb',
    empty: 'Ihr Warenkorb ist leer',
    emptyDesc: 'Entdecken Sie unsere Sammlung!',
    subtotal: 'Zwischensumme',
    shipping: 'Versand',
    free: 'KOSTENLOS',
    discount: 'Rabatt (10%)',
    total: 'Gesamt',
    checkout: 'Zur Kasse',
    continueShopping: 'Weiter Einkaufen',
    browseShop: 'Zum Shop',
    items: 'Artikel',
    item: 'Artikel',
    remove: 'Entfernen',
    youMightLike: 'Das könnte Ihnen gefallen',
    addToCart: 'Hinzufügen',
  },
};

const CartDrawer = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { cart, fetchCart, language, convertPrice, currencySymbols, displayCurrency } = useContext(AppContext);
  const t = translations[language] || translations.en;
  const [updating, setUpdating] = useState(null);
  const [upsellProducts, setUpsellProducts] = useState([]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || cart.length === 0) return;
    const cartIds = cart.map(i => i.product_id);
    axios.get(`${API}/products/popular?limit=6`).then(res => {
      const filtered = res.data.filter(p => !cartIds.includes(p.id) && p.stock > 0 && !p.deleted && !p.archived).slice(0, 3);
      setUpsellProducts(filtered);
    }).catch(() => {});
  }, [isOpen, cart]);

  const handleRemove = async (itemId) => {
    try {
      await axios.delete(`${API}/cart/${itemId}`, { withCredentials: true });
      fetchCart();
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const handleAddUpsell = async (productId) => {
    setUpdating(productId);
    try {
      await axios.post(`${API}/cart/add?product_id=${productId}&quantity=1`, {}, { withCredentials: true });
      await fetchCart();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add');
    } finally {
      setUpdating(null);
    }
  };

  const handleUpdateQuantity = async (productId, newQuantity) => {
    if (newQuantity < 1 || updating) return;
    setUpdating(productId);
    try {
      await axios.post(`${API}/cart/add?product_id=${productId}&quantity=${newQuantity}`, {}, { withCredentials: true });
      await fetchCart();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setUpdating(null);
    }
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.product?.price || 0) * item.quantity, 0);
  const qualifiesForFreeShipping = subtotal >= 100;
  const shipping = qualifiesForFreeShipping ? 0 : 4;
  const discountAmount = subtotal >= 100 ? subtotal * 0.1 : 0;
  const total = subtotal - discountAmount + shipping;
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const goToCheckout = () => {
    onClose();
    navigate('/cart');
  };

  const goToProduct = (product) => {
    onClose();
    navigate(productUrl(product));
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[200] transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-[201] shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-[#1F2D50] text-white">
          <div className="flex items-center gap-2">
            <ShoppingBag size={20} />
            <h2 className="font-semibold text-lg">{t.yourCart}</h2>
            <span className="bg-[#C8A951] text-white text-xs px-2 py-0.5 rounded-full">
              {cartCount} {cartCount === 1 ? t.item : t.items}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded transition"
            aria-label="Close cart"
          >
            <X size={22} />
          </button>
        </div>

        {/* Cart Items */}
        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <ShoppingBag size={64} className="text-gray-200 mb-4" />
            <p className="text-lg font-semibold text-gray-600 mb-1">{t.empty}</p>
            <p className="text-sm text-gray-400 mb-6">{t.emptyDesc}</p>
            <button
              onClick={() => { onClose(); navigate('/shop'); }}
              className="btn-primary"
            >
              {t.browseShop}
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {cart.map((item) => (
                <div key={item.id} className="flex gap-3 bg-gray-50 rounded-lg p-3 relative group">
                  {/* Image */}
                  <div
                    className="w-20 h-20 flex-shrink-0 cursor-pointer rounded overflow-hidden bg-gray-200"
                    onClick={() => item.product && goToProduct(item.product)}
                  >
                    {item.product?.images?.[0] && (
                      <img
                        src={item.product.images[0].startsWith('http') ? item.product.images[0] : `${BACKEND_URL}${item.product.images[0]}`}
                        alt={item.product?.title || ''}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <h4
                      className="text-sm font-semibold truncate cursor-pointer hover:text-[#C8A951] transition"
                      onClick={() => item.product && goToProduct(item.product)}
                    >
                      {item.product?.[`title_${language}`] || item.product?.title}
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5">{item.product?.condition}</p>

                    <div className="flex items-center justify-between mt-2">
                      {/* Quantity controls */}
                      <div className="flex items-center gap-1 bg-white border rounded-lg">
                        <button
                          onClick={() => handleUpdateQuantity(item.product_id, item.quantity - 1)}
                          disabled={item.quantity <= 1 || updating === item.product_id}
                          className="p-1 hover:bg-gray-100 rounded-l-lg disabled:opacity-30 transition"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="px-2 text-sm font-medium min-w-[24px] text-center">
                          {updating === item.product_id ? '...' : item.quantity}
                        </span>
                        <button
                          onClick={() => handleUpdateQuantity(item.product_id, item.quantity + 1)}
                          disabled={updating === item.product_id}
                          className="p-1 hover:bg-gray-100 rounded-r-lg disabled:opacity-30 transition"
                        >
                          <Plus size={14} />
                        </button>
                      </div>

                      {/* Price */}
                      <span className="font-bold text-sm" style={{ color: '#B8941E' }}>
                        {currencySymbols[displayCurrency]}{convertPrice(item.product?.price * item.quantity)}
                      </span>
                    </div>
                  </div>

                  {/* Remove */}
                  <button
                    onClick={() => handleRemove(item.id)}
                    className="absolute top-2 right-2 p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition"
                    title={t.remove}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}

              {/* Upsell section */}
              {upsellProducts.length > 0 && (
                <div className="mt-4 pt-4 border-t border-dashed">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t.youMightLike}</p>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {upsellProducts.map((p) => (
                      <div key={p.id} className="flex-shrink-0 w-28 bg-white rounded-lg border p-2">
                        <div
                          className="w-full h-20 rounded overflow-hidden bg-gray-100 cursor-pointer mb-1"
                          onClick={() => goToProduct(p)}
                        >
                          {p.images?.[0] && (
                            <img
                              src={p.images[0].startsWith('http') ? p.images[0] : `${BACKEND_URL}${p.images[0]}`}
                              alt={p.title || ''}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                        <p className="text-xs font-medium truncate" title={p[`title_${language}`] || p.title}>
                          {p[`title_${language}`] || p.title}
                        </p>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs font-bold" style={{ color: '#B8941E' }}>
                            {currencySymbols[displayCurrency]}{convertPrice(p.price)}
                          </span>
                          <button
                            onClick={() => handleAddUpsell(p.id)}
                            disabled={updating === p.id}
                            className="text-[10px] bg-[#C8A951] hover:bg-[#b89842] text-white px-2 py-0.5 rounded font-semibold transition disabled:opacity-50"
                          >
                            {updating === p.id ? '...' : t.addToCart}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Free shipping progress bar */}
            {!qualifiesForFreeShipping && (
              <div className="px-4 py-2 bg-blue-50 border-t">
                <div className="flex justify-between text-xs text-blue-700 mb-1">
                  <span>{language === 'tr' ? 'Ücretsiz kargoya' : 'Free shipping at'} $100</span>
                  <span>${(100 - subtotal).toFixed(2)} {language === 'tr' ? 'kaldı' : 'away'}</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-1.5">
                  <div
                    className="bg-blue-600 h-1.5 rounded-full transition-all"
                    style={{ width: `${Math.min(100, (subtotal / 100) * 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Totals */}
            <div className="border-t p-4 space-y-2 bg-gray-50">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t.subtotal}</span>
                <span>{currencySymbols[displayCurrency]}{convertPrice(subtotal)}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>{t.discount}</span>
                  <span>-{currencySymbols[displayCurrency]}{convertPrice(discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t.shipping}</span>
                <span className={qualifiesForFreeShipping ? 'text-green-600 font-semibold' : ''}>
                  {qualifiesForFreeShipping ? t.free : `${currencySymbols[displayCurrency]}${convertPrice(shipping)}`}
                </span>
              </div>
              <div className="flex justify-between font-bold text-lg pt-2 border-t">
                <span>{t.total}</span>
                <span style={{ color: '#B8941E' }}>{currencySymbols[displayCurrency]}{convertPrice(total)}</span>
              </div>

              {/* Checkout button */}
              <button
                onClick={goToCheckout}
                className="w-full bg-[#C8A951] hover:bg-[#b89842] text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition transform hover:scale-[1.02]"
              >
                <Zap size={18} />
                {t.checkout}
              </button>
              <button
                onClick={onClose}
                className="w-full text-center text-sm text-gray-500 hover:text-gray-700 py-1 transition"
              >
                {t.continueShopping}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
